import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { FlightBookingApiService } from '../../core/api-services/flight-booking-api.service';
import {
  CreateFlightRequest,
  FlightRequestSummary,
} from '../../core/models/flight-request';

/**
 * Student-facing flight booking requests.
 *
 * The student describes the trip and adds the flight options they would accept
 * being booked on. Reviewers then approve or reject the request; a rejection
 * always carries a reason, which is shown here.
 */
@Component({
  selector: 'app-flight-requests',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule, RouterModule],
  templateUrl: './flight-requests.html',
  styleUrl: './flight-requests.scss',
})
export class FlightRequestsPage implements OnInit {
  private readonly api = inject(FlightBookingApiService);
  private readonly fb = inject(FormBuilder);

  /**
   * True when rendered inside the student dashboard card, where the card already
   * provides the page title — so this component only shows its action button.
   */
  @Input() embedded = false;

  requests: FlightRequestSummary[] = [];
  loading = false;
  submitting = false;
  error = '';
  message = '';
  showForm = false;

  readonly form = this.fb.group({
    departureCity: ['', Validators.required],
    departureAirportCode: [''],
    destinationCity: ['', Validators.required],
    destinationAirportCode: [''],
    requestedDepartureDate: ['', Validators.required],
    requestedReturnDate: [''],
    preferredCabinClass: ['Economy'],
    purpose: [''],
    requiresPaidFlight: [false],
    additionalNotes: [''],
    options: this.fb.array([this.buildOption(true)]),
  });

  get options(): FormArray {
    return this.form.get('options') as FormArray;
  }

  ngOnInit(): void {
    this.load();
  }

  private buildOption(preferred = false) {
    return this.fb.group({
      airline: ['', Validators.required],
      flightNumber: [''],
      cabinClass: ['Economy'],
      estimatedCost: [null as number | null],
      currency: ['ZAR'],
      isPreferred: [preferred],
    });
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .getMyRequests()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (requests) => (this.requests = requests ?? []),
        error: () => (this.error = 'Your flight requests could not be loaded.'),
      });
  }

  addOption(): void {
    this.options.push(this.buildOption(this.options.length === 0));
  }

  removeOption(index: number): void {
    if (this.options.length === 1) {
      return;
    }
    this.options.removeAt(index);
  }

  markPreferred(index: number): void {
    this.options.controls.forEach((control, position) =>
      control.get('isPreferred')?.setValue(position === index)
    );
  }

  submit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const request: CreateFlightRequest = {
      departureCity: value.departureCity ?? '',
      departureAirportCode: value.departureAirportCode || undefined,
      destinationCity: value.destinationCity ?? '',
      destinationAirportCode: value.destinationAirportCode || undefined,
      requestedDepartureDate: value.requestedDepartureDate ?? '',
      requestedReturnDate: value.requestedReturnDate || null,
      preferredCabinClass: value.preferredCabinClass || undefined,
      purpose: value.purpose || undefined,
      requiresPaidFlight: !!value.requiresPaidFlight,
      additionalNotes: value.additionalNotes || undefined,
      options: (value.options ?? []).map((option) => ({
        airline: option.airline ?? undefined,
        flightNumber: option.flightNumber ?? undefined,
        cabinClass: option.cabinClass ?? undefined,
        estimatedCost: option.estimatedCost ?? null,
        currency: option.currency ?? undefined,
        isPreferred: !!option.isPreferred,
      })),
    };

    this.submitting = true;
    this.error = '';
    this.message = '';

    this.api
      .createRequest(request)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (created) => {
          this.message = `Flight request ${created.referenceNumber} submitted. Reviewers have been notified.`;
          this.resetForm();
          this.showForm = false;
          this.load();
        },
        error: (err) => (this.error = err?.error?.message || 'The flight request could not be submitted.'),
      });
  }

  cancel(request: FlightRequestSummary): void {
    this.error = '';
    this.api.cancelRequest(request.id).subscribe({
      next: () => this.load(),
      error: (err) => (this.error = err?.error?.message || 'The request could not be withdrawn.'),
    });
  }

  private resetForm(): void {
    this.form.reset({
      preferredCabinClass: 'Economy',
      requiresPaidFlight: false,
    });
    while (this.options.length > 0) {
      this.options.removeAt(0);
    }
    this.options.push(this.buildOption(true));
  }

  /** Mirrors the dashboard's `.status` / `.status-*` pill convention. */
  statusClass(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'status status-approved';
      case 'REJECTED':
        return 'status status-rejected';
      case 'BOOKED':
        return 'status status-booked';
      case 'CANCELLED':
        return 'status status-cancelled';
      default:
        return 'status status-pending';
    }
  }

  canWithdraw(request: FlightRequestSummary): boolean {
    return request.status === 'SUBMITTED';
  }
}
